// Browser-safe catalog of the Spaulding & Oakes delegated-document-work gig:
// id, label, blurb, the data-entry field spec, and the conformance teaching
// hint.
//
// Mirrors the other catalogs in shape and role, for the single gig surfaced in
// the Spaulding & Oakes app. It imports NOTHING heavy — no profile/schema JSON,
// no seal path, no Node built-ins — so both the browser UI and the /api/run
// Function can import it. It is the single source of truth for the gig id and
// input shape:
//   - the UI renders the matter data-entry form from `fields`,
//   - the Spaulding & Oakes /api/run validates the submitted inputs against `fields`,
//   - src/live/gigs-legal.ts (the seal-bound gig) reuses this id.
// A test asserts every legal catalog id resolves to a gig so the two cannot
// drift.
//
// The seal-side gig reads: documentText, docType, court, matterRef, title,
// docketNumber, authoritiesChecked. `docType` and `court` are closed selects —
// the allowed values are on the mandate's allowlists; the last option of each
// is the off-allowlist violation lever. The document text, matter reference,
// and title are hashed into the receipt, never sealed raw (privilege-safe).

import type { GigCatalogEntry } from "./gig-catalog.js";

// A fictional draft shown in the data-entry screen. Realistic in shape but
// entirely invented — the disclosure banner says so, and only a hash of
// whatever text is present is ever sealed.
const DRAFT_MOTION = [
  "MOTION TO COMPEL FURTHER RESPONSES TO REQUESTS FOR PRODUCTION",
  "",
  "Plaintiff respectfully moves this Court for an order compelling Defendant to",
  "produce documents responsive to Requests for Production Nos. 12–18. The",
  "requested documents are relevant and proportional to the needs of the case,",
  "and Defendant's boilerplate objections are insufficient under the governing",
  "rules. Meet-and-confer efforts have been exhausted; see the accompanying",
  "declaration.",
  "",
  "For the reasons set forth herein, the motion should be granted."
].join("\n");

export const LEGAL_GIG_CATALOG: GigCatalogEntry[] = [
  {
    id: "document_execution",
    label: "Litigation: draft, cite-check, file, and docket",
    blurb:
      "The drafting agent takes a matter from draft to docket under a filing mandate: it drafts the document, cite-checks it, files it with a court, and records the docket confirmation: one sealed execution record. The mandate binds the allowed document types and the allowed courts. The document text, the matter reference, and the title are hashed into the receipt, never sealed raw. The receipt carries no privileged content. Use fictional data only.",
    usesReview: false,
    runLabel: "Seal the execution record",
    fields: [
      {
        key: "documentText",
        label: "Document draft (editable)",
        type: "textarea",
        default: DRAFT_MOTION,
        help: "Hashed into the receipt (document_hash) and bound across the chain; the raw text is never sealed. Enter fictional data only."
      },
      {
        // A closed set: the first three are on the mandate's allowed-doc-type
        // allowlist; "discovery_request" is off-allowlist and is one violation
        // lever. Keep in sync with ALLOWED_DOC_TYPES in gigs-legal.ts.
        key: "docType",
        label: "Document type",
        type: "select",
        default: "motion",
        options: ["motion", "brief", "notice", "discovery_request"],
        help: "Allowed: motion, brief, notice. “discovery_request” is off the allowlist."
      },
      {
        // A closed set: N.D. Cal. and 9th Cir. are on the mandate's allowed-court
        // allowlist; "E.D. Tex." is off-allowlist and is the headline violation
        // lever. Keep in sync with ALLOWED_COURTS in gigs-legal.ts.
        key: "court",
        label: "Court",
        type: "select",
        default: "N.D. Cal.",
        options: ["N.D. Cal.", "9th Cir.", "E.D. Tex."],
        help: "Allowed: N.D. Cal., 9th Cir. “E.D. Tex.” is off the allowlist."
      },
      {
        key: "matterRef",
        label: "Matter reference",
        type: "text",
        default: "matter-2026-0417",
        help: "An opaque id, hashed into the receipt, not sealed raw."
      },
      {
        key: "title",
        label: "Working title",
        type: "text",
        default: "Motion to Compel",
        help: "Hashed into the receipt; the raw title is never sealed."
      },
      {
        key: "docketNumber",
        label: "Docket number",
        type: "text",
        default: "3:26-cv-01847-JD",
        help: "The court's docket identifier (public record)."
      },
      {
        key: "authoritiesChecked",
        label: "Authorities cite-checked",
        type: "number",
        default: "14",
        min: 0
      }
    ],
    violationHint:
      "Set the court to one off the allowlist (e.g. “E.D. Tex.”), or the document type to the off-allowlist “discovery_request”, to seal an execution record that honestly records conformant: false."
  }
];

export function legalCatalogEntry(id: string | undefined): GigCatalogEntry | undefined {
  if (id === undefined) return LEGAL_GIG_CATALOG[0];
  return LEGAL_GIG_CATALOG.find((g) => g.id === id);
}
